import React from "react";
import { Select, Textarea, Group, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  PACKAGE_REASON_CATEGORIES,
  PackageReasonCategory,
} from "../../types/packages";

interface ReasonFieldsProps {
  /** Currently-selected reason category, or null when nothing is chosen yet. */
  category: PackageReasonCategory | null;
  /** Free-text note explaining the adjustment. */
  note: string;
  onCategoryChange: (category: PackageReasonCategory | null) => void;
  onNoteChange: (note: string) => void;
  /** Disable both fields (e.g. while a submit is in flight). */
  disabled?: boolean;
  /**
   * The selectable reason list. Defaults to the full shared taxonomy; revoke
   * passes a narrower subset (REVOKE_REASON_CATEGORIES).
   */
  categories?: { value: PackageReasonCategory; label: string }[];
  /** Show the category `required` asterisk + gate submits. Default true. */
  categoryRequired?: boolean;
  /** Show the note `withAsterisk` + gate submits. Default true. */
  noteRequired?: boolean;
}

/**
 * Shared reason/note picker for manual audit trails (package + gift-card
 * adjustments). Requiredness is per-consumer: gift-card flows keep the strict
 * required + submit-gate UX; package flows opt out via `categoryRequired` /
 * `noteRequired`.
 */
export default function ReasonFields({
  category,
  note,
  onCategoryChange,
  onNoteChange,
  disabled = false,
  categories = PACKAGE_REASON_CATEGORIES,
  categoryRequired = true,
  noteRequired = true,
}: ReasonFieldsProps) {
  const trimmedNote = note.trim();
  return (
    <>
      <Select
        label="Reason"
        placeholder="Select a reason"
        data={categories}
        value={category}
        onChange={(value) =>
          onCategoryChange((value as PackageReasonCategory | null) ?? null)
        }
        disabled={disabled}
        required={categoryRequired}
      />
      <Textarea
        label={
          <Group
            gap={4}
            align="center"
            wrap="nowrap"
            component="span"
            style={{ display: "inline-flex", verticalAlign: "middle" }}
          >
            Note
            <Tooltip
              label="Provide a brief description for why this adjustment is being made"
              multiline
              w={240}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <IconInfoCircle
                size={14}
                style={{
                  cursor: "help",
                  color: "var(--mantine-color-dimmed)",
                }}
              />
            </Tooltip>
          </Group>
        }
        withAsterisk={noteRequired}
        placeholder="e.g. Goodwill credit for a delayed appointment…"
        value={note}
        onChange={(e) => onNoteChange(e.currentTarget.value)}
        minRows={2}
        autosize
        disabled={disabled}
        error={
          noteRequired && note.length > 0 && trimmedNote.length === 0
            ? "A note is required"
            : undefined
        }
      />
    </>
  );
}

/**
 * Submit-gating helper for consumers that keep the fields required (gift-card
 * flows). Package flows have opted out and should not call this.
 */
export function isReasonValid(
  category: PackageReasonCategory | null,
  note: string,
): boolean {
  return category !== null && note.trim().length > 0;
}
